import { z } from "zod"
import { TicketIntegrationKindSchema } from "../../knowledgeBase/suggestions"

export const BackofficeKnowledgeEvolutionResponseDataSchema = z.object({
  previousAnsweredRate: z.number().nullable(),
  answeredRateChangePercentagePoints: z.number().nullable(),
  comparisonPeriod: z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    previousFrom: z.iso.datetime(),
    previousTo: z.iso.datetime(),
  }),
})

export const BackofficeKnowledgeMetricsResponseDataSchema = z.object({
  questionCount: z.number(),
  answeredCount: z.number(),
  knowledgePendingCount: z.number(),
  knowledgeGapCount: z.number(),
  labelledQuestionCount: z.number(),
  knowledgeQuestionCount: z.number(),
  knowledgeAnsweredCount: z.number(),
  knowledgeAnsweredWithKnowledgeBaseCount: z.number(),
  knowledgeAnsweredWithCapabilityCount: z.number(),
  answeredWithKnowledgeBaseCount: z.number(),
  answeredWithCapabilityCount: z.number(),
  answeredUnattributedCount: z.number(),
  retrievalMissCount: z.number(),
  answeredRate: z.number().nullable(),
  interimCoverageRate: z.number().nullable(),
  knowledgeCoverageRate: z.number().nullable(),
  knowledgeBaseCoverageRate: z.number().nullable(),
  capabilityCoverageRate: z.number().nullable(),
  knowledgeUnansweredRate: z.number().nullable(),
  unattributedAnswerRate: z.number().nullable(),
  retrievalMissRate: z.number().nullable(),
  answerLinkRate: z.number().nullable(),
  conversationKnowledgeUsedRate: z.number().nullable(),
})

export const BackofficeKnowledgeOverviewResponseDataSchema = z.object({
  total: BackofficeKnowledgeMetricsResponseDataSchema.nullable(),
  companies: z.array(
    BackofficeKnowledgeMetricsResponseDataSchema.extend({
      companyId: z.string(),
      companyName: z.string(),
      ticketIntegrations: z.array(TicketIntegrationKindSchema),
      shouldNotifyKnowledgeGapsOnSlack: z.boolean(),
      score: z.number().nullable(),
      ...BackofficeKnowledgeEvolutionResponseDataSchema.shape,
    }),
  ),
})

export const BackofficeKnowledgeTimelineResponseDataSchema = z.array(
  BackofficeKnowledgeMetricsResponseDataSchema.extend({ day: z.string() }),
)

export const BackofficeKnowledgeCategoriesResponseDataSchema = z.array(
  BackofficeKnowledgeMetricsResponseDataSchema.extend({
    category: z.string().nullable(),
    score: z.number().nullable(),
    questionShare: z.number().nullable(),
    ...BackofficeKnowledgeEvolutionResponseDataSchema.shape,
  }),
)

export const BackofficeKnowledgeItemsResponseDataSchema = z.array(
  z.object({
    knowledgeBaseItemId: z.bigint().transform(String),
    title: z.string().nullable(),
    kind: z.string(),
    retrievedCount: z.number(),
    usedCount: z.number(),
    precisionRate: z.number().nullable(),
    answeredQuestionCount: z.number(),
  }),
)

export const BackofficeKnowledgeCapabilitiesResponseDataSchema = z.array(
  z.object({
    capabilityId: z.bigint().transform(String),
    identifier: z.string(),
    name: z.string().nullable(),
    messageCount: z.number(),
    answeredQuestionCount: z.number(),
  }),
)

export const BackofficeKnowledgeGapsResponseDataSchema = z.array(
  z.object({
    id: z.bigint().transform(String),
    companyId: z.bigint().transform(String),
    companyName: z.string(),
    organizationId: z.bigint().transform(String).nullable(),
    customerId: z.bigint().transform(String),
    conversationId: z.bigint().transform(String),
    summary: z.string(),
    category: z.string().nullable(),
    isKnowledgeQuestion: z.boolean().nullable(),
    createdAt: z.date().transform((value) => value.toISOString()),
  }),
)

export const BackofficeKnowledgeCategoryCompaniesResponseDataSchema = z.array(
  z.object({
    companyId: z.string().nullable(),
    companyName: z.string().nullable(),
    questionCount: z.number(),
    answeredCount: z.number(),
    knowledgePendingCount: z.number(),
    answeredRate: z.number().nullable(),
    questionShare: z.number().nullable(),
    score: z.number().nullable(),
    ...BackofficeKnowledgeEvolutionResponseDataSchema.shape,
  }),
)

export const BackofficeUnansweredQuestionTopicsResponseDataSchema = z.array(
  z.object({
    companyId: z.bigint().transform(String),
    companyName: z.string(),
    fromDate: z
      .date()
      .transform((value) => value.toISOString())
      .nullable(),
    toDate: z
      .date()
      .transform((value) => value.toISOString())
      .nullable(),
    generatedAt: z
      .date()
      .transform((value) => value.toISOString())
      .nullable(),
    topics: z
      .array(
        z.object({
          id: z.bigint().transform(String),
          question: z.string(),
          category: z.string().nullable(),
          sampledQuestionCount: z.number(),
          sampleSize: z.number(),
        }),
      )
      .max(5),
  }),
)

export const RecalculateBackofficeUnansweredQuestionTopicsResponseDataSchema = z.object({
  queued: z.boolean(),
})

export const BackofficeUnansweredTopicConversationsResponseDataSchema = z.object({
  question: z.string(),
  companyName: z.string(),
  organizationId: z.bigint().transform(String),
  hasSupportingQuestions: z.boolean(),
  conversations: z.array(
    z.object({
      conversationId: z.bigint().transform(String),
      customerId: z.bigint().transform(String),
      questionId: z.bigint().transform(String),
      summary: z.string(),
      createdAt: z.date().transform((value) => value.toISOString()),
    }),
  ),
})

export const UpdateBackofficeKnowledgeCompanyResponseDataSchema = z.object({
  companyId: z.bigint().transform(String),
  shouldNotifyKnowledgeGapsOnSlack: z.boolean(),
})
